// src/components/sections/ClientContactSection.jsx - Updated with Referenceable checkbox
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import VerifiedIcon from '@mui/icons-material/Verified';

/**
 * Client Contact Section component displays the primary contact information
 * for a project's client.
 */
const ClientContactSection = ({
  clientContact,
  contacts,
  clientName,
  projectName,
  projectId,
  isLoading,
  error,
  onViewAllClick,
  isReferenceable = false
}) => {
  // Render loading state
  if (isLoading) {
    return (
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            mb: 1, 
            fontSize: '0.75rem', 
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>PRIMARY PROJECT CONTACT</span>
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <CircularProgress size={20} />
        </Box>
      </Box>
    );
  }

  // Render error state
  if (error || !clientContact) {
    return (
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            mb: 1, 
            fontSize: '0.75rem', 
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>PRIMARY PROJECT CONTACT</span>
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '0.75rem', 
              color: 'warning.main' 
            }}
          >
            Could not load contact information
          </Typography>
        </Box>
      </Box>
    );
  }

  const hasMultipleContacts = contacts && contacts.length > 1;

  return (
    <Box sx={{ px: 1.5, pb: 1.5 }}>
      <Typography 
        variant="subtitle2" 
        sx={{ 
          mb: 1, 
          fontSize: '0.75rem', 
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <span>PRIMARY PROJECT CONTACT</span>
        {hasMultipleContacts && (
          <Button
            variant="text"
            size="small"
            onClick={onViewAllClick}
            sx={{ 
              fontSize: '0.65rem', 
              p: 0, 
              minWidth: 'auto',
              color: '#9B6DFF',
              fontWeight: 'medium',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline'
              }
            }}
          >
            VIEW ALL
          </Button>
        )}
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon sx={{ fontSize: '0.9rem', color: 'primary.main' }} />
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            {clientContact.name || 
             clientContact.full_name || 
             (clientContact.first_name && clientContact.last_name ? 
               `${clientContact.first_name} ${clientContact.last_name}` : 
               clientContact.title || 'Unknown Contact')}
            {clientContact.title ? ` (${clientContact.title})` : ''}
          </Typography>
        </Box>
        
        {clientContact.email && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon sx={{ fontSize: '0.9rem', color: 'primary.main' }} />
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              {clientContact.email}
            </Typography>
          </Box>
        )}
        
        {clientContact.phone && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon sx={{ fontSize: '0.9rem', color: 'primary.main' }} />
            <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
              {clientContact.phone}
            </Typography>
          </Box>
        )}
        
        {/* Referenceable indicator (read-only) */}
        <Box sx={{ mt: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isReferenceable}
                disabled={true}
                size="small"
                icon={<VerifiedIcon sx={{ color: 'rgba(3, 218, 198, 0.3)' }} />}
                checkedIcon={<VerifiedIcon sx={{ color: '#03DAC6' }} />}
                sx={{
                  '&.Mui-disabled': {
                    color: isReferenceable ? '#03DAC6' : 'rgba(3, 218, 198, 0.3)'
                  }
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                Project is referenceable
              </Typography>
            }
            sx={{
              margin: 0,
              cursor: 'default',
              '& .MuiFormControlLabel-label': { fontSize: '0.65rem' }
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

ClientContactSection.propTypes = {
  clientContact: PropTypes.object,
  contacts: PropTypes.array,
  clientName: PropTypes.string,
  projectName: PropTypes.string,
  projectId: PropTypes.string,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onViewAllClick: PropTypes.func,
  isReferenceable: PropTypes.bool
};

export default ClientContactSection;
